import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

const lengthUnits = {
  m: { name: "Meter", toSI: 1 },
  km: { name: "Kilometer", toSI: 1000 },
  cm: { name: "Centimeter", toSI: 0.01 },
  mm: { name: "Millimeter", toSI: 0.001 },
  ft: { name: "Foot", toSI: 0.3048 },
  in: { name: "Inch", toSI: 0.0254 },
  mi: { name: "Mile", toSI: 1609.34 },
};

const massUnits = {
  kg: { name: "Kilogram", toSI: 1 },
  g: { name: "Gram", toSI: 0.001 },
  mg: { name: "Milligram", toSI: 0.000001 },
  lb: { name: "Pound", toSI: 0.453592 },
  oz: { name: "Ounce", toSI: 0.0283495 },
};

const timeUnits = {
  s: { name: "Second", toSI: 1 },
  min: { name: "Minute", toSI: 60 },
  h: { name: "Hour", toSI: 3600 },
  day: { name: "Day", toSI: 86400 },
};

const forceUnits = {
  N: { name: "Newton", toSI: 1 },
  kN: { name: "Kilonewton", toSI: 1000 },
  lbf: { name: "Pound-force", toSI: 4.44822 },
};

export default function UnitConverter() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Tools", url: "https://calclab.app/tools" },
      { name: "Unit Converter", url: "https://calclab.app/calculator/unit" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Unit Converter",
      "Convert between units of length, mass, time, and force. Free online universal unit converter.",
      "https://calclab.app/calculator/unit",
      lang
    )
  ]);

  const [value, setValue] = useState("");
  const [fromUnit, setFromUnit] = useState("m");
  const [toUnit, setToUnit] = useState("ft");
  const [category, setCategory] = useState<"length" | "mass" | "time" | "force">("length");

  const getUnits = () => {
    switch (category) {
      case "length": return lengthUnits;
      case "mass": return massUnits;
      case "time": return timeUnits;
      case "force": return forceUnits;
    }
  };

  const convert = () => {
    const val = parseFloat(value);
    if (isNaN(val)) return null;

    const units = getUnits();
    const fromUnitData = units[fromUnit as keyof typeof units] as { name: string; toSI: number } | undefined;
    const toUnitData = units[toUnit as keyof typeof units] as { name: string; toSI: number } | undefined;
    
    if (!fromUnitData || !toUnitData) return null;
    
    const siValue = val * fromUnitData.toSI;
    const result = siValue / toUnitData.toSI;
    return result;
  };

  const result = convert();
  const units = getUnits();

  return (
    <PageLayout calculatorName="Unit Converter" calculatorPath="/calculator/unit">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Universal Unit Conversion</CardTitle>
            <CardDescription>
              Select category and units to convert
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs value={category} onValueChange={(v) => setCategory(v as any)} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="length">Length</TabsTrigger>
                <TabsTrigger value="mass">Mass</TabsTrigger>
                <TabsTrigger value="time">Time</TabsTrigger>
                <TabsTrigger value="force">Force</TabsTrigger>
              </TabsList>

              <TabsContent value={category} className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Value to Convert</Label>
                    <Input
                      type="number"
                      step="any"
                      placeholder="Enter value"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From</Label>
                      <Select value={fromUnit} onValueChange={setFromUnit}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(units).map(([key, unit]) => (
                            <SelectItem key={key} value={key}>
                              {unit.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>To</Label>
                      <Select value={toUnit} onValueChange={setToUnit}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(units).map(([key, unit]) => (
                            <SelectItem key={key} value={key}>
                              {unit.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {result !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {result.toFixed(6)} {toUnit}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {value} {fromUnit} = {result.toFixed(6)} {toUnit}
                      </p>
                    </CardContent>
                  </Card>
                )}

                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Common Conversions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm text-muted-foreground">
                    {category === "length" && (
                      <>
                        <p>• 1 meter = 3.28084 feet</p>
                        <p>• 1 kilometer = 0.621371 miles</p>
                        <p>• 1 inch = 2.54 centimeters</p>
                      </>
                    )}
                    {category === "mass" && (
                      <>
                        <p>• 1 kilogram = 2.20462 pounds</p>
                        <p>• 1 pound = 16 ounces</p>
                        <p>• 1 gram = 1000 milligrams</p>
                      </>
                    )}
                    {category === "time" && (
                      <>
                        <p>• 1 hour = 60 minutes = 3600 seconds</p>
                        <p>• 1 day = 24 hours = 1440 minutes</p>
                      </>
                    )}
                    {category === "force" && (
                      <>
                        <p>• 1 Newton = 0.224809 pound-force</p>
                        <p>• 1 kilonewton = 1000 Newtons</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
